import { useState, useCallback, useMemo, useEffect } from 'react'
import type { Card, CardLevel } from '../types/card'
import { getSupabase } from '../lib/supabase'
import { generateVocabularyFromAI } from '../utils/ai'
import gojuonRaw from '../data/gojuon.json'
import { n5Words } from '../data/n5-seed'

// ── 类型定义 ──

interface RawEntry {
  id: string
  front: string
  back: string
  type: 'hiragana' | 'katakana' | 'word'
}

interface CardRow {
  id: string
  front: string
  back: string
  type: 'hiragana' | 'katakana' | 'word'
  level?: string
  stage: number
  next_review_date: string
}

// ── 数据转换 ──

function rowToCard(row: CardRow): Card {
  return {
    id: row.id,
    front: row.front,
    back: typeof row.back === 'string' ? JSON.parse(row.back) : row.back,
    type: row.type,
    // 兼容旧数据：无 level 字段默认 'Gojuon'
    level: (row.level as CardLevel) || 'Gojuon',
    stage: row.stage,
    nextReviewDate: row.next_review_date,
  }
}

function cardToRow(card: Card): CardRow {
  return {
    id: card.id,
    front: card.front,
    back: JSON.stringify(card.back),
    type: card.type,
    level: card.level,
    stage: card.stage,
    next_review_date: card.nextReviewDate,
  }
}

type InsertRow = Omit<CardRow, 'id'>

function gojuonToInsertRow(raw: RawEntry): InsertRow {
  return {
    front: raw.front,
    back: JSON.stringify({ romaji: raw.back, meaning: '' }),
    type: raw.type,
    level: 'Gojuon',
    stage: 0,
    next_review_date: new Date().toISOString(),
  }
}

function n5ToInsertRow(entry: { front: string; kanji: string; meaning: string }): InsertRow {
  return {
    front: entry.front,
    back: JSON.stringify({ kanji: entry.kanji, meaning: entry.meaning }),
    type: 'hiragana',
    level: 'N5',
    stage: 0,
    next_review_date: new Date().toISOString(),
  }
}

// ── 增删改查 ──

async function fetchAllCards(): Promise<Card[]> {
  const { data, error } = await getSupabase()
    .from('cards')
    .select('*')
    .order('id')

  if (error) throw error
  return (data as CardRow[]).map(rowToCard)
}

async function seedIfEmpty(): Promise<void> {
  const { count, error } = await getSupabase()
    .from('cards')
    .select('*', { count: 'exact', head: true })

  if (error) throw error
  if (count && count > 0) return

  // 五十音种子
  const gojuonRows: InsertRow[] = (gojuonRaw as RawEntry[]).map(gojuonToInsertRow)
  const { error: gojuonErr } = await getSupabase().from('cards').insert(gojuonRows)
  if (gojuonErr) throw gojuonErr

  // N5 测试种子
  const n5Rows: InsertRow[] = n5Words.map(n5ToInsertRow)
  const { error: n5Err } = await getSupabase().from('cards').insert(n5Rows)
  if (n5Err) throw n5Err
}

// ── 手术式种子修复：补全缺失的 level 字段 + 按需注入缺失的级别种子 ──

async function ensureSeedsAndLevels(): Promise<void> {
  const supabase = getSupabase()
  const { data, error } = await supabase.from('cards').select('id, level')
  if (error || !data || data.length === 0) return

  const rows = data as { id: string; level?: string }[]
  const validLevels = ['Gojuon', 'N5', 'N4', 'N3', 'N2', 'N1']

  // 1. 修复缺少 level 的旧卡片（手术式 UPDATE，不删数据，保留 SRS 进度）
  const brokenIds = rows
    .filter((r) => !r.level || !validLevels.includes(r.level))
    .map((r) => r.id)

  if (brokenIds.length > 0) {
    console.warn(`发现 ${brokenIds.length} 张旧卡片缺少 level 字段，正在修复...`)
    for (let i = 0; i < brokenIds.length; i += 100) {
      try {
        await supabase
          .from('cards')
          .update({ level: 'Gojuon' } as any)
          .in('id', brokenIds.slice(i, i + 100))
      } catch {
        // 列可能尚不存在，INSERT N5 之后会创建
      }
    }
  }

  // 2. 确保 N5 种子存在（如果缺失则注入）
  const hasN5 = rows.some((r) => r.level === 'N5')
  if (!hasN5) {
    console.log('N5 种子数据缺失，正在注入...')
    const n5Rows: InsertRow[] = n5Words.map(n5ToInsertRow)
    await supabase.from('cards').insert(n5Rows)
  }

  // 3. 确保五十音种子存在（如果表有数据但无 Gojuon 级别）
  const hasGojuon = rows.some((r) => r.level === 'Gojuon')
  if (!hasGojuon) {
    console.log('五十音种子数据缺失，正在注入...')
    const gojuonRows: InsertRow[] = (gojuonRaw as RawEntry[]).map(gojuonToInsertRow)
    await supabase.from('cards').insert(gojuonRows)
  }
}

async function updateCardInDB(card: Card): Promise<void> {
  const { error } = await getSupabase()
    .from('cards')
    .update(cardToRow(card))
    .eq('id', card.id)

  if (error) throw error
}

// ── 智能自动推新：当前级别待复习 < 5 张时，自动解锁 5 个新词 ──

async function autoUnlockIfNeeded(cards: Card[], level: CardLevel): Promise<Card[]> {
  const now = new Date()

  // 统计该级别下今日待复习卡片（stage > 0 且到期）
  const dueCount = cards.filter(
    (c) => c.level === level && c.stage > 0 && new Date(c.nextReviewDate) <= now
  ).length

  if (dueCount >= 5) return cards

  // 从该级别新词池（stage === 0）中随机抽取
  const newCards = cards.filter((c) => c.level === level && c.stage === 0)
  if (newCards.length === 0) return cards

  const need = Math.min(5, newCards.length)
  const shuffled = [...newCards].sort(() => Math.random() - 0.5)
  const toUnlock = shuffled.slice(0, need)

  // 批量解锁
  const updated = [...cards]
  for (const card of toUnlock) {
    const unlocked = {
      ...card,
      stage: 1,
      nextReviewDate: now.toISOString(),
    }
    try {
      await updateCardInDB(unlocked)
      const idx = updated.findIndex((c) => c.id === card.id)
      if (idx !== -1) updated[idx] = unlocked
    } catch (e) {
      console.error(`解锁卡片 ${card.id} 失败:`, e)
    }
  }

  return updated
}

// ── Hook ──

export function useDeck(level: CardLevel = 'Gojuon') {
  const [cards, setCards] = useState<Card[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [aiGenerating, setAiGenerating] = useState(false)
  const [aiGenError, setAiGenError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      await seedIfEmpty()
      await ensureSeedsAndLevels()
      let data = await fetchAllCards()
      data = await autoUnlockIfNeeded(data, level)
      setCards(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : '数据加载失败')
    } finally {
      setLoading(false)
    }
  }, [level])

  useEffect(() => { load() }, [load])

  const allCards = cards

  // 当前级别的统计
  const levelCards = useMemo(
    () => cards.filter((c) => c.level === level),
    [cards, level]
  )

  const masteredCount = useMemo(
    () => levelCards.filter((c) => c.stage > 3).length,
    [levelCards]
  )

  const newCount = useMemo(
    () => levelCards.filter((c) => c.stage === 0).length,
    [levelCards]
  )

  // 当前级别卡牌总数
  const levelTotal = levelCards.length

  // 是否完美通关：当前级别所有卡片已掌握 且 无新词待解锁
  const isLevelComplete = useMemo(
    () => levelTotal > 0 && masteredCount === levelTotal && newCount === 0,
    [levelTotal, masteredCount, newCount]
  )

  // 待复习：到期筛选 + 级别筛选
  const dueCards = useMemo(
    () =>
      cards.filter((c) => {
        if (c.level !== level) return false
        if (new Date(c.nextReviewDate) > new Date()) return false
        return true
      }),
    [cards, level]
  )

  const updateCard = useCallback(async (updated: Card) => {
    setCards((prev) =>
      prev.map((c) => (c.id === updated.id ? updated : c))
    )
    try {
      await updateCardInDB(updated)
    } catch (e) {
      setError(e instanceof Error ? e.message : '更新失败')
    }
  }, [])

  // 手动解锁 5 个新词：优先本地库存 → AI 动态造词入库
  const unlockNextBatch = useCallback(async () => {
    const newCards = cards.filter((c) => c.level === level && c.stage === 0)

    // ── 有本地库存：走老逻辑 ──
    if (newCards.length >= 5) {
      const shuffled = [...newCards].sort(() => Math.random() - 0.5)
      const toUnlock = shuffled.slice(0, 5)
      const now = new Date().toISOString()

      setCards((prev) =>
        prev.map((c) => {
          if (toUnlock.find((u) => u.id === c.id)) {
            return { ...c, stage: 1, nextReviewDate: now }
          }
          return c
        })
      )

      for (const card of toUnlock) {
        try {
          await updateCardInDB({ ...card, stage: 1, nextReviewDate: now })
        } catch (e) {
          console.error(`解锁卡片 ${card.id} 失败:`, e)
        }
      }
      return
    }

    // ── 无库存：查询已有词汇 → AI over-fetch 15 词 → 去重 → 入库前 5 ──
    setAiGenerating(true)
    setAiGenError(null)
    try {
      // 1. 获取数据库中全量 front 字段建立去重对照表
      const { data: existingRows } = await getSupabase()
        .from('cards')
        .select('front')
      const existingFronts: string[] = (existingRows as { front: string }[] | null)?.map(
        (r) => r.front
      ) ?? []

      // 2. over-fetch 15 词给后续过滤留足余量
      const aiWords = await generateVocabularyFromAI(level, 15)
      if (aiWords.length === 0) {
        setAiGenError('AI 造词失败，请稍后重试')
        return
      }

      // 3. 严格去重：剔除数据库中已存在的单词
      const newUniqueWords = aiWords.filter(
        (w) => !existingFronts.includes(w.front)
      )

      // 4. 截取前 5 个全新单词
      const wordsToInsert = newUniqueWords.slice(0, 5)

      if (wordsToInsert.length === 0) {
        setAiGenError('AI 生成的词全部重复，请稍后重试')
        return
      }

      const now = new Date().toISOString()
      const inserted: Card[] = []

      for (const word of wordsToInsert) {
        const newCard: Card = {
          id: crypto.randomUUID(),
          front: word.front,
          back: { kanji: word.kanji, meaning: word.meaning },
          type: 'word',
          level,
          stage: 1,
          nextReviewDate: now,
        }

        try {
          await getSupabase()
            .from('cards')
            .insert(cardToRow(newCard))
          inserted.push(newCard)
        } catch (e) {
          console.error(`AI 卡片入库失败 ${word.front}:`, e)
        }
      }

      if (inserted.length > 0) {
        setCards((prev) => [...prev, ...inserted])
      } else {
        setAiGenError('AI 造词入库失败，请检查网络后重试')
      }
    } catch {
      setAiGenError('AI 请求失败，请检查网络后重试')
    } finally {
      setAiGenerating(false)
    }
  }, [cards, level])

  return {
    allCards,
    dueCards,
    masteredCount,
    newCount,
    levelTotal,
    isLevelComplete,
    updateCard,
    unlockNextBatch,
    aiGenerating,
    aiGenError,
    refresh: load,
    loading,
    error,
  }
}
